export const MAX_PAGES = 20
export const MAX_TOTAL_BYTES = 50 * 1024 * 1024
export const MAX_IMAGE_PIXELS = 36_000_000
export const MAX_OUTPUT_SIDE = 3508
export const MAX_OUTPUT_PIXELS = 64_000_000
export const MAX_OUTPUT_BYTES = 64 * 1024 * 1024

export type ScanMode = 'color' | 'grayscale' | 'black-white'

export interface CropRect {
  left: number
  top: number
  right: number
  bottom: number
}

export interface PageTransform {
  rotation: 0 | 90 | 180 | 270
  crop: CropRect
  mode: ScanMode
  brightness: number
  contrast: number
}

/** Crop values are expressed against the page as the user sees it after rotation. */
export function sourceCropForRotation(crop: CropRect, rotation: PageTransform['rotation']): CropRect {
  if (rotation === 90) {
    return { left: crop.top, top: crop.right, right: crop.bottom, bottom: crop.left }
  }
  if (rotation === 180) {
    return { left: crop.right, top: crop.bottom, right: crop.left, bottom: crop.top }
  }
  if (rotation === 270) {
    return { left: crop.bottom, top: crop.left, right: crop.top, bottom: crop.right }
  }
  return { ...crop }
}

export interface ScannerPage {
  id: string
  file: File
  previewUrl: string
  width: number
  height: number
  transform: PageTransform
}

export type ScannerValidationCode = 'unsupported_input' | 'size_limit' | 'invalid_input'

export class ScannerValidationError extends Error {
  constructor(
    public readonly code: ScannerValidationCode,
    public readonly reason: 'format' | 'page_count' | 'total_size' | 'dimensions' | 'corrupt',
  ) {
    super(reason)
    this.name = 'ScannerValidationError'
  }
}

export const DEFAULT_TRANSFORM: PageTransform = {
  rotation: 0,
  crop: { left: 0, top: 0, right: 0, bottom: 0 },
  mode: 'color',
  brightness: 0,
  contrast: 0,
}

function pngDimensions(bytes: Uint8Array): [number, number] | null {
  if (bytes.length < 24 || bytes[0] !== 0x89 || bytes[1] !== 0x50 || bytes[2] !== 0x4e || bytes[3] !== 0x47) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  return [view.getUint32(16), view.getUint32(20)]
}

function jpegDimensions(bytes: Uint8Array): [number, number] | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null
  let offset = 2
  while (offset + 9 < bytes.length) {
    if (bytes[offset] !== 0xff) { offset += 1; continue }
    const marker = bytes[offset + 1] ?? 0
    if (marker === 0xd9 || marker === 0xda) break
    const size = ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0)
    if (size < 2 || offset + size + 2 > bytes.length) return null
    if ((marker >= 0xc0 && marker <= 0xc3) || (marker >= 0xc5 && marker <= 0xc7) || (marker >= 0xc9 && marker <= 0xcb) || (marker >= 0xcd && marker <= 0xcf)) {
      return [((bytes[offset + 7] ?? 0) << 8) | (bytes[offset + 8] ?? 0), ((bytes[offset + 5] ?? 0) << 8) | (bytes[offset + 6] ?? 0)]
    }
    offset += size + 2
  }
  return null
}

function webpDimensions(bytes: Uint8Array, fileSize: number): [number, number] | null {
  if (bytes.length < 30 || String.fromCharCode(...bytes.slice(0, 4)) !== 'RIFF' || String.fromCharCode(...bytes.slice(8, 12)) !== 'WEBP') return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.getUint32(4, true) + 8 !== fileSize) return null
  const chunk = String.fromCharCode(...bytes.slice(12, 16))
  const chunkSize = view.getUint32(16, true)
  if (20 + chunkSize > fileSize) return null
  if (chunk === 'VP8X') {
    if (chunkSize < 10) return null
    const width = 1 + (bytes[24] ?? 0) + ((bytes[25] ?? 0) << 8) + ((bytes[26] ?? 0) << 16)
    const height = 1 + (bytes[27] ?? 0) + ((bytes[28] ?? 0) << 8) + ((bytes[29] ?? 0) << 16)
    return [width, height]
  }
  if (chunk === 'VP8 ' && bytes.length >= 30 && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) {
    if (chunkSize < 10) return null
    return [((bytes[27] ?? 0) << 8 | (bytes[26] ?? 0)) & 0x3fff, ((bytes[29] ?? 0) << 8 | (bytes[28] ?? 0)) & 0x3fff]
  }
  if (chunk === 'VP8L' && bytes.length >= 25 && bytes[20] === 0x2f) {
    if (chunkSize < 5) return null
    const bits = (bytes[21] ?? 0) | ((bytes[22] ?? 0) << 8) | ((bytes[23] ?? 0) << 16) | ((bytes[24] ?? 0) << 24)
    return [(bits & 0x3fff) + 1, ((bits >>> 14) & 0x3fff) + 1]
  }
  return null
}

export async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const bytes = new Uint8Array(await file.slice(0, 512 * 1024).arrayBuffer())
  const dimensions = pngDimensions(bytes) ?? jpegDimensions(bytes) ?? webpDimensions(bytes, file.size)
  if (!dimensions || dimensions[0] <= 0 || dimensions[1] <= 0) {
    throw new ScannerValidationError('invalid_input', 'corrupt')
  }
  return { width: dimensions[0], height: dimensions[1] }
}

export function isSupportedScannerFile(file: File): boolean {
  const extension = file.name.toLowerCase()
  return ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
    extension.endsWith('.jpg') || extension.endsWith('.jpeg') || extension.endsWith('.png') || extension.endsWith('.webp')
}

export async function validateScannerFiles(files: File[], existing: ScannerPage[] = []): Promise<Array<{ file: File; width: number; height: number }>> {
  if (existing.length + files.length > MAX_PAGES) throw new ScannerValidationError('size_limit', 'page_count')
  const totalSize = existing.reduce((sum, page) => sum + page.file.size, 0) + files.reduce((sum, file) => sum + file.size, 0)
  if (totalSize > MAX_TOTAL_BYTES) throw new ScannerValidationError('size_limit', 'total_size')
  const result = []
  for (const file of files) {
    if (!isSupportedScannerFile(file)) throw new ScannerValidationError('unsupported_input', 'format')
    const dimensions = await readImageDimensions(file)
    if (dimensions.width * dimensions.height > MAX_IMAGE_PIXELS) throw new ScannerValidationError('size_limit', 'dimensions')
    result.push({ file, ...dimensions })
  }
  return result
}

export function movePage<T>(pages: T[], index: number, direction: -1 | 1): T[] {
  const target = index + direction
  if (target < 0 || target >= pages.length) return pages
  const next = [...pages]
  const [page] = next.splice(index, 1)
  if (page === undefined) return pages
  next.splice(target, 0, page)
  return next
}

export function rotatedCropSize(width: number, height: number, transform: PageTransform): { width: number; height: number } {
  const crop = sourceCropForRotation(transform.crop, transform.rotation)
  const cropWidth = Math.max(1, Math.round(width * (1 - crop.left / 100 - crop.right / 100)))
  const cropHeight = Math.max(1, Math.round(height * (1 - crop.top / 100 - crop.bottom / 100)))
  return transform.rotation === 90 || transform.rotation === 270
    ? { width: cropHeight, height: cropWidth }
    : { width: cropWidth, height: cropHeight }
}
